/**
 * Debug overlay for collision/movement system.
 * Draws real-time debug info on the floor plan canvas during drag.
 */

import { getObjectCorners } from '@shared-lib/collision/geometry';

/**
 * Draw debug info for the collision/movement system.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} debugState - Accumulated debug data from drag frame
 * @param {number} scale
 * @param {Object} [options]
 * @param {boolean} [options.collapsed] - Whether the panel is collapsed
 * @param {string[]} [options.disabledConstraints] - Disabled constraint categories
 */
export function drawCollisionDebugOverlay(
  ctx,
  debugState,
  scale,
  options = {}
) {
  const { collapsed = false, disabledConstraints = [] } = options;

  // Always draw in screen space
  ctx.save();
  ctx.resetTransform();
  const canvasWidth = ctx.canvas?.width || 800;
  const canvasHeight = ctx.canvas?.height || 600;

  const FONT_SIZE = 26;
  const LINE_HEIGHT = 34;
  const HEADER_HEIGHT = LINE_HEIGHT + 16;

  ctx.font = `${FONT_SIZE}px monospace`;
  ctx.textAlign = 'left';

  // Header line (always shown)
  const headerText = collapsed
    ? '── Collision Debug ── [+]'
    : '── Collision Debug ── [−]';

  if (collapsed) {
    // Draw collapsed header only
    const headerWidth = ctx.measureText(headerText).width + 24;
    const panelX = canvasWidth - headerWidth - 12;
    const panelY = canvasHeight - HEADER_HEIGHT - 12;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(panelX, panelY, headerWidth, HEADER_HEIGHT);
    ctx.fillStyle = '#00ff88';
    ctx.fillText(headerText, panelX + 10, panelY + LINE_HEIGHT + 2);
    ctx.restore();
    return;
  }

  // Build content lines
  const lines = [];
  lines.push(headerText);
  lines.push('');

  if (debugState) {
    if (debugState.snappedWall) {
      lines.push(
        `Wall: ${debugState.snappedWall.id?.slice(-8) || 'none'}  (${debugState.wallSource})`
      );
      lines.push(
        `  dir: (${debugState.wallDir?.x?.toFixed(2)}, ${debugState.wallDir?.z?.toFixed(2)})`
      );
    } else {
      lines.push('Wall: none (free drag)');
    }

    if (debugState.objectCenter) {
      lines.push(
        `Pos: (${debugState.objectCenter.x?.toFixed(3)}, ${debugState.objectCenter.z?.toFixed(3)})`
      );
      const rotY = debugState.objectRotation?.y;
      lines.push(
        `Rot: ${rotY !== undefined ? ((rotY * 180) / Math.PI).toFixed(1) + '°' : 'none'}`
      );
    }

    lines.push('');
    if (debugState.collisions?.length > 0) {
      lines.push(`Collisions: ${debugState.collisions.length}`);
      for (const c of debugState.collisions) {
        lines.push(`  ${c.type}`);
        lines.push(`    ${c.reason || ''}`);
      }
    } else {
      lines.push('Collisions: none');
    }

    lines.push('');
    if (debugState.snapResult) {
      const s = debugState.snapResult;
      if (s.snapped) {
        lines.push(`Snap: ✓ ${(s.snapDistance * 1000).toFixed(0)}mm`);
        lines.push(
          `  to: (${s.center.x?.toFixed(3)}, ${s.center.z?.toFixed(3)})`
        );
      } else {
        lines.push('Snap: ✗ failed');
      }
    }

    if (debugState.snapDebugLog?.length > 0) {
      lines.push('');
      lines.push('Snap attempts:');
      for (const entry of debugState.snapDebugLog) {
        lines.push(`  ${entry}`);
      }
    }

    if (debugState.wallClamp) {
      lines.push(`Wall clamp: ${debugState.wallClamp}`);
    }
  } else {
    lines.push('No active drag');
  }

  // Disabled constraints section
  lines.push('');
  lines.push('── Disabled Constraints ──');
  if (disabledConstraints.length > 0) {
    for (const c of disabledConstraints) {
      lines.push(`  ✗ ${c}`);
    }
  } else {
    lines.push('  none');
  }

  // Position in bottom-right
  const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const panelWidth = maxWidth + 24;
  const panelHeight = lines.length * LINE_HEIGHT + 16;
  const panelX = canvasWidth - panelWidth - 12;
  let panelY = canvasHeight - panelHeight - 12;

  // Draw background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillRect(panelX, panelY, panelWidth, panelHeight);

  // Draw text
  panelY += LINE_HEIGHT + 2;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Color disabled constraints in red
    if (line.startsWith('  ✗ ')) {
      ctx.fillStyle = '#ff6666';
    } else if (line.startsWith('── Disabled')) {
      ctx.fillStyle = '#ff9944';
    } else {
      ctx.fillStyle = '#00ff88';
    }
    ctx.fillText(line, panelX + 10, panelY);
    panelY += LINE_HEIGHT;
  }

  ctx.restore();

  // --- Draw world-space debug visuals ---
  if (!debugState?.objectCenter) return;

  // Draw snapped wall highlight
  if (debugState.snappedWall) {
    const wall = debugState.snappedWall;
    const s = wall.start || wall.alignment?.bottomLeft;
    const e = wall.end || wall.alignment?.bottomRight;
    if (s && e) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 0, 0.9)';
      ctx.lineWidth = Math.max(4 / scale, 5 / scale);
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(s.x, s.z);
      ctx.lineTo(e.x, e.z);
      ctx.stroke();

      ctx.fillStyle = '#ffff00';
      ctx.font = `${0.05}px monospace`;
      ctx.fillText('S', s.x - 0.06, s.z - 0.03);
      ctx.fillText('E', e.x + 0.02, e.z - 0.03);

      ctx.restore();
    }
  }

  // Draw snap axis arrow (if wall attached)
  if (debugState.wallDir && debugState.objectCenter) {
    const cx = debugState.objectCenter.x;
    const cz = debugState.objectCenter.z;
    const arrowLen = 0.4;

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.8)';
    ctx.lineWidth = Math.max(2 / scale, 2.5 / scale);
    ctx.setLineDash([4 / scale, 3 / scale]);

    ctx.beginPath();
    ctx.moveTo(
      cx - debugState.wallDir.x * arrowLen,
      cz - debugState.wallDir.z * arrowLen
    );
    ctx.lineTo(
      cx + debugState.wallDir.x * arrowLen,
      cz + debugState.wallDir.z * arrowLen
    );
    ctx.stroke();

    const nx = -debugState.wallDir.z;
    const nz = debugState.wallDir.x;
    ctx.strokeStyle = 'rgba(255, 136, 0, 0.6)';
    ctx.beginPath();
    ctx.moveTo(cx, cz);
    ctx.lineTo(cx + nx * arrowLen * 0.6, cz + nz * arrowLen * 0.6);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.restore();
  }

  // Draw clearance boundary ray-cast debug visuals
  if (debugState.boundaryVisuals) {
    const bv = debugState.boundaryVisuals;

    if (bv.outsideCorners?.length) {
      ctx.save();
      for (const corner of bv.outsideCorners) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.9)';
        ctx.beginPath();
        ctx.arc(corner.x, corner.z, 0.03, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ff0000';
        ctx.font = `${0.05}px monospace`;
        ctx.fillText(`c[${corner.index}]`, corner.x + 0.04, corner.z - 0.02);
      }
      ctx.restore();
    }

    if (bv.rays?.length) {
      ctx.save();
      for (const ray of bv.rays) {
        ctx.strokeStyle = ray.hit
          ? 'rgba(0, 255, 0, 0.9)'
          : 'rgba(255, 0, 0, 0.9)';
        ctx.lineWidth = Math.max(2 / scale, 3 / scale);
        ctx.setLineDash(ray.hit ? [] : [6 / scale, 4 / scale]);

        ctx.beginPath();
        ctx.moveTo(ray.origin.x, ray.origin.z);
        ctx.lineTo(ray.end.x, ray.end.z);
        ctx.stroke();

        const dx = ray.end.x - ray.origin.x;
        const dz = ray.end.z - ray.origin.z;
        const len = Math.sqrt(dx * dx + dz * dz);
        if (len > 0) {
          const ux = dx / len;
          const uz = dz / len;
          const arrowSize = 0.04;
          ctx.beginPath();
          ctx.moveTo(ray.end.x, ray.end.z);
          ctx.lineTo(
            ray.end.x - ux * arrowSize + uz * arrowSize * 0.5,
            ray.end.z - uz * arrowSize - ux * arrowSize * 0.5
          );
          ctx.moveTo(ray.end.x, ray.end.z);
          ctx.lineTo(
            ray.end.x - ux * arrowSize - uz * arrowSize * 0.5,
            ray.end.z - uz * arrowSize + ux * arrowSize * 0.5
          );
          ctx.stroke();
        }

        const midX = (ray.origin.x + ray.end.x) / 2;
        const midZ = (ray.origin.z + ray.end.z) / 2;
        ctx.fillStyle = ray.hit ? '#00ff00' : '#ff0000';
        ctx.font = `${0.04}px monospace`;
        ctx.fillText(
          ray.label + (ray.hit ? ` → e[${ray.hitEdge}]` : ' miss'),
          midX + 0.03,
          midZ
        );
      }
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  // Draw physical corners
  if (debugState.objectCenter && debugState.objectDimensions) {
    const obj = {
      center: debugState.objectCenter,
      dimensions: debugState.objectDimensions,
      rotation: debugState.objectRotation || { y: 0 },
    };
    const corners = getObjectCorners(obj);

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 200, 255, 0.6)';
    ctx.lineWidth = Math.max(1.5 / scale, 2 / scale);
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].z);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i].x, corners[i].z);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
}
